import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import CustomSelect from '../../ui/CustomSelect';
import { Switch } from '../../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { ArchiveCategory } from './ArchiveCategoryTableRow';

interface ArchiveCategoryModalProps {
  open: boolean;
  data: ArchiveCategory | null;
  saving: boolean;
  onClose: () => void;
  onSave: (e: React.FormEvent) => void;
  onDataChange: (data: ArchiveCategory) => void;
}

export default function ArchiveCategoryModal({
  open,
  data,
  saving,
  onClose,
  onSave,
  onDataChange,
}: ArchiveCategoryModalProps) {
  const [errors, setErrors] = useState<{
    category_code?: string;
    category_name?: string;
    archive_type?: string;
  }>({});

  useEffect(() => {
    // Reset errors when modal opens/closes
    if (open) {
      setErrors({});
    }
  }, [open]);

  if (!data) return null;

  const handleFieldChange = (field: keyof ArchiveCategory, value: any) => {
    onDataChange({ ...data, [field]: value });
    // Clear error when user types/selects
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Reset errors
    const newErrors: typeof errors = {};
    let hasError = false;

    // Validate required fields
    if (!data.category_code || data.category_code.trim() === '') {
      newErrors.category_code = 'Kode kategori wajib diisi';
      hasError = true;
    }

    if (!data.category_name || data.category_name.trim() === '') {
      newErrors.category_name = 'Nama kategori wajib diisi';
      hasError = true;
    }

    if (!data.archive_type || data.archive_type.trim() === '') {
      newErrors.archive_type = 'Tipe arsip wajib dipilih';
      hasError = true;
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    // Clear errors if validation passes
    setErrors({});
    onSave(e);
  };

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{data.id ? 'Edit' : 'Tambah'} Kategori Arsip</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>
                Kode Kategori <span className="text-red-500">*</span>
              </Label>
              <Input
                value={data.category_code || ''}
                onChange={e => handleFieldChange('category_code', e.target.value)}
                className={errors.category_code ? 'border-red-500' : ''}
              />
              {errors.category_code && (
                <p className="text-sm text-red-500">{errors.category_code}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>
                Nama Kategori <span className="text-red-500">*</span>
              </Label>
              <Input
                value={data.category_name || ''}
                onChange={e => handleFieldChange('category_name', e.target.value)}
                className={errors.category_name ? 'border-red-500' : ''}
              />
              {errors.category_name && (
                <p className="text-sm text-red-500">{errors.category_name}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>
                Tipe Arsip <span className="text-red-500">*</span>
              </Label>
              <CustomSelect
                value={data.archive_type || ''}
                onChange={v => handleFieldChange('archive_type', v)}
                options={[
                  { value: 'debtor', label: 'Debitur' },
                  { value: 'credit', label: 'Kredit' },
                  { value: 'hris', label: 'HRIS' },
                  { value: 'legal', label: 'Legal' },
                  { value: 'general', label: 'General' },
                ]}
                placeholder="Pilih tipe arsip"
                width="w-full"
                color="blue"
                error={!!errors.archive_type}
              />
              {errors.archive_type && <p className="text-sm text-red-500">{errors.archive_type}</p>}
            </div>
            <div className="space-y-2">
              <Label>Masa Berlaku</Label>
              <CustomSelect
                value={data.validity_unit || 'permanent'}
                onChange={v => handleFieldChange('validity_unit', v)}
                options={[
                  { value: 'permanent', label: 'Permanent' },
                  { value: 'days', label: 'Hari' },
                  { value: 'months', label: 'Bulan' },
                  { value: 'years', label: 'Tahun' },
                ]}
                placeholder="Pilih masa berlaku"
                width="w-full"
                color="blue"
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <Label>Mandatory</Label>
              <Switch
                checked={data.is_mandatory || false}
                onCheckedChange={v => handleFieldChange('is_mandatory', v)}
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <Label>Perlu Validasi</Label>
              <Switch
                checked={data.requires_validation || false}
                onCheckedChange={v => handleFieldChange('requires_validation', v)}
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <Label>Status Aktif</Label>
              <Switch
                checked={data.is_active || false}
                onCheckedChange={v => handleFieldChange('is_active', v)}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              Batal
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
